import React, { useState } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import ContextMenu, { ContextMenuElement } from 'components/ContextMenu/ContextMenu';
import type { DpiaHistoryListItem } from 'config/api/dpiaRequests/dpiaRequests.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import { buildDpiaNeedAssessmentLocation } from 'pages/ProcessingActivities/DpiaNeedAssessmentPage/dpiaNeedAssessment.helpers';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { getAdminDpiaNeedAssessmentAction, isDpiaFormAwaitingAdminReview } from './dpiaCaseActions';
import DpiaHistoryDialog from './DpiaHistoryDialog';
import { resolveDpiaLogRecords } from './DpiaLogRecordActions';
import useDpiaHistory from './useDpiaHistory';

type Props = {
  apaId: number;
  canAssess: boolean;
  canOpenDpia: boolean;
  children: React.ReactElement;
  elements: ContextMenuElement[];
  onAssess: () => void;
  requestStatus?: string | null;
  status: string;
};

const DpiaRowContextMenu: React.FC<Props> = ({ apaId, canAssess, canOpenDpia, children, elements, onAssess, requestStatus, status }) => {
  const { t } = useTranslation();
  const navigation = useHistory();
  const [activated, setActivated] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const enabled = activated && (canAssess || canOpenDpia);
  const { history, isLoading, isFetching, isError, isUnavailable, refetch } = useDpiaHistory({ apaId, organizationId, enabled });
  const records = resolveDpiaLogRecords(history?.items ?? [], requestStatus);
  const messages = processing_activities_messages.asset_ropa.dpia_history;
  const openRecord = (item: DpiaHistoryListItem) => {
    const returnPath = navigation.location.pathname;
    if (item.kind === 'dpia_request' && canAssess) {
      navigation.push(buildDpiaNeedAssessmentLocation({ apaId, requestId: item.id, returnPath }));
    } else if (item.kind === 'dpia' && canOpenDpia) {
      navigation.push(generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, { dpiaId: String(item.id) }), { returnPath });
    }
  };
  const menu = [...elements];
  if (getAdminDpiaNeedAssessmentAction({ dpiaDisplayStatus: status, dpiaRequestStatus: requestStatus }, canAssess) === 'assess') {
    menu.push({ label: t(processing_activities_messages.asset_ropa.apa_review.assess_dpia), onClick: onAssess });
  }
  if (enabled && (isLoading || isFetching)) {
    menu.push({ label: t(general_messages.loading), disabled: true, onClick: () => undefined });
  } else if (enabled && (isError || isUnavailable)) {
    menu.push({
      label: t(general_messages.retry),
      onClick: () => {
        refetch();
      },
    });
  } else {
    if (canOpenDpia && records.dpia) {
      const document = records.dpia;
      menu.push({
        label: t(isDpiaFormAwaitingAdminReview({ dpiaDisplayStatus: status }) ? messages.review_dpia : messages.open_dpia),
        onClick: () => openRecord(document),
      });
    }
    if (canAssess && records.assessment && records.assessment.status !== 'indicated') {
      const { assessment } = records;
      menu.push({ label: t(messages.open_assessment), onClick: () => openRecord(assessment) });
    }
    if ((canAssess || canOpenDpia) && records.showHistory) {
      menu.push({
        label: t(processing_activities_messages.asset_ropa.owner_workspace.actions.view_dpia_history),
        onClick: () => setHistoryOpen(true),
      });
    }
  }
  return (
    <Box onClickCapture={() => setActivated(true)} onFocusCapture={() => setActivated(true)}>
      <ContextMenu elements={menu} id={`dpia-actions-${apaId}`}>
        {children}
      </ContextMenu>
      <DpiaHistoryDialog
        apaId={apaId}
        canOpenAssessments={canAssess}
        onClose={() => setHistoryOpen(false)}
        onOpenItem={openRecord}
        open={historyOpen}
        organizationId={organizationId}
      />
    </Box>
  );
};

export default DpiaRowContextMenu;
